import type { Nullable } from '@iheartjane/types';
import type { QueryClient } from '@tanstack/react-query';
import compact from 'lodash-es/compact';
import type { LoaderFunctionArgs } from 'react-router';
import { redirect } from 'react-router';

import { getStoreQueryBody } from '@jane/shared/data-access';
import type { Kiosk, Store } from '@jane/shared/types';

import { ensureNumericId } from '../../../util';
import { getJwtQueryBody } from '../queries/jwt';
import { getKioskQueryBody } from '../queries/kiosk';
import { getWhoamiQueryBody } from '../queries/whoami';

export type RootLoaderType = {
  kiosk: Nullable<Kiosk>;
  kioskId: number;
  store: Store;
  storeId: number;
  /** If the browser is authenticated, this is identical to the 'kiosk' response. */
  whoami: Nullable<Kiosk>;
};

export const rootLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
  }: LoaderFunctionArgs): Promise<Response | RootLoaderType> => {
    const { storeId, cartUuid } = params;
    const numericStoreId = ensureNumericId(storeId);

    const store = await queryClient.ensureQueryData(
      getStoreQueryBody<Store>(numericStoreId)
    );
    const kioskId = ensureNumericId(store.kiosk_id);
    const kiosk = await queryClient.ensureQueryData(getKioskQueryBody(kioskId));

    const jwt = await queryClient.ensureQueryData(getJwtQueryBody());
    const whoami = await queryClient.ensureQueryData(
      getWhoamiQueryBody(jwt, cartUuid)
    );

    const kioskStoreIds = compact(whoami?.stores).map(({ id }) => Number(id));
    if (whoami?.enabled && !kioskStoreIds.includes(numericStoreId)) {
      return redirect(`/stores/${kioskStoreIds[0]}`);
    }

    return {
      kiosk,
      kioskId,
      store,
      storeId: numericStoreId,
      whoami,
    };
  };
